/**
 * @file The display text of a report cadence: "Mondays, 7am", "Every day, 7am"
 * or "First of month, 7am". Consumer: the CADENCE column of the Reports list.
 * A week runs on the calendar's `weekStartsOn`, so the weekly text names that
 * day and reads "Every week" while the calendar is unset. The hour is the
 * calendar's local hour the definition stores, on a 12-hour clock.
 */
import type { AnalyticsCalendar, AnalyticsReportCadence } from '#types';

const HOURS_PER_HALF_DAY = 12;

const hourText = (hour: number): string =>
  `${hour % HOURS_PER_HALF_DAY || HOURS_PER_HALF_DAY}${hour < HOURS_PER_HALF_DAY ? 'am' : 'pm'}`;

const dayText = (
  every: AnalyticsReportCadence['every'],
  weekStartsOn: AnalyticsCalendar['weekStartsOn'] | undefined,
): string => {
  switch (every) {
    case 'day':
      return 'Every day';
    case 'month':
      return 'First of month';
    case 'week':
      return weekStartsOn === undefined
        ? 'Every week'
        : `${weekStartsOn.charAt(0).toUpperCase()}${weekStartsOn.slice(1)}s`;
  }
};

export const analyticsReportCadenceText = (
  cadence: Readonly<AnalyticsReportCadence>,
  weekStartsOn: AnalyticsCalendar['weekStartsOn'] | undefined,
): string =>
  `${dayText(cadence.every, weekStartsOn)}, ${hourText(cadence.hour)}`;
